import { getIsCypressRunning } from "constants/constants";
import { useAuth0 } from "contexts/AuthManager/Auth";
import { useEffect, useMemo, useRef } from "react";
import { useMount } from "react-use";
import { useTraceToHoneycomb } from "tracer";
import {
  useFocusRange,
  useGetPageFromPath,
  useVisibleProcesses,
} from "utils/customHooks/genericHooks";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { ONE_HOUR_OF_MS } from "utils/timeUtils";

/** Measure this % of any given set of events e.g. 0.01 = 1%. */
const FLIGHT_PERF__LONG_TASK__SAMPLE_RATE = 0.001; // super small because this happens on most interactions

/** Observe & report performance metrics
 *
 * - PerformanceObserver "longtask" and "event" events are piped to Honeycomb
 * - goal: as performance improves, we should see fewer of these events over time
 */
export default function FlightPerformanceObserver() {
  const traceToHoneycomb = useTraceToHoneycomb({
    sampleRateOverride: FLIGHT_PERF__LONG_TASK__SAMPLE_RATE,
  });
  const ckey = useCustomerKey();
  const { user } = useAuth0();

  // observe performance metrics via PerformanceObserver API
  useMount(() => {
    const handleEntry = (entry: PerformanceEntry) => {
      const trace = (eventName: string, dataToTrace = {}) => {
        traceToHoneycomb({
          dataToTrace: {
            // span name
            name: `performance.observer ${eventName}`,
            // additional data ("tags")
            customer: String(ckey),
            email: String(user?.email),
            url: `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`,
            "performance.observer.duration": entry.duration,
            "performance.observer.eventName": eventName,
            "performance.observer.startTime": entry.startTime,
            "performance.observer.eventKind": entry.name,
            ...((entry as any).target?.className && {
              "performance.eventTarget": (entry as any).target.className,
            }),
            ...dataToTrace,
          },
        });
      };
      if (entry.entryType === "event") {
        trace("Long event");
      } else {
        trace("Long task");
      }
    };

    /** Sends performance measurement events from the browser to Honeycomb for tracing.
     * PerformanceObserver API: https://developer.mozilla.org/en-US/docs/Web/API/PerformanceObserver
     */
    new window.PerformanceObserver(list => {
      list.getEntries().forEach(entry => {
        handleEntry(entry);
      });
    }).observe({ entryTypes: ["event", "longtask"] });
  });

  return null;
}

/** a "standard candle" measurement i.e. measure something that's consistently the same size / takes the same duration
 *
 * here we'll measure loading duration of the aggregate data by tracking the loading indicator's progress bar
 */
export function FlightStandardCandlePerformanceObserver({
  progressPercent,
}: {
  progressPercent: number;
}) {
  // - only run when cypress is running in CI & specified in localStorage
  const shouldRun =
    process.env.CI &&
    getIsCypressRunning() &&
    window.localStorage.getItem("flight-performance-observer") === "true";
  return shouldRun ? <Observers {...{ progressPercent }} /> : null;
}

function Observers({ progressPercent }: { progressPercent: number }) {
  const { user } = useAuth0();
  const ckey = useCustomerKey();
  const visibleProcesses = useVisibleProcesses();
  const traceToHoneycomb = useTraceToHoneycomb({
    sampleRateOverride: 1, // since we're only running in CI, we can trace every time
  });

  const interactionRef = useRef<{
    end(): number;
  } | null>(null);

  useEffect(() => {
    // started loading
    if (progressPercent === 0) {
      interactionRef.current = measureInteraction();
    }
    // finished loading
    else if (progressPercent >= 1) {
      const duration = interactionRef.current?.end();
      // report loading time to honeycomb
      if (duration) {
        traceToHoneycomb({
          dataToTrace: {
            // span name
            name: "performance.e2e data load time",
            "performance.e2e.loading_duration": duration,
            "performance.e2e.num_processes": visibleProcesses.length,
            customer: String(ckey),
            email: String(user?.email),
            // record url to differentiate loading times between pages
            url: `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`,
          },
          callbackFn: () => {
            console.log(
              `⌚ traced performance.e2e.loading_duration (${visibleProcesses.length} processes):`,
              duration
            );
          },
        });
        interactionRef.current = null;
      }
    }
  }, [progressPercent, ckey, user, traceToHoneycomb, visibleProcesses.length]);

  return null;
}

function measureInteraction(): {
  end(): number;
} {
  // performance.now() returns the number of ms
  // elapsed since the page was opened
  const startTimestamp = performance.now();

  return {
    end() {
      const endTimestamp = performance.now();
      const duration = endTimestamp - startTimestamp;
      return duration;
    },
  };
}

/** appears to be non-random sampling; if set to > 0.5, always samples on first trace; if <= 0.5, never samples on first trace */
const FLIGHT_PERF__COMPONENT_LOAD_TIME__SAMPLE_RATE = 0.51;

/** measure when a component starts and stops loading, then send the measurement to honeycomb */
export function useObserveComponentLoadTime({
  name,
  loading,
}: {
  name: string;
  /** whether the component is loading */
  loading: boolean;
}) {
  /** number of processes and focus range on this page might affect load times */
  const visibleProcesses = useVisibleProcesses();
  const focusRange = useFocusRange();
  const { pageName } = useGetPageFromPath();
  const focusRangeHours = useMemo(
    () => (!focusRange ? 0 : (focusRange.lt - focusRange.ge) / ONE_HOUR_OF_MS),
    [focusRange]
  );
  const traceToHoneycomb = useTraceToHoneycomb({
    sampleRateOverride: FLIGHT_PERF__COMPONENT_LOAD_TIME__SAMPLE_RATE,
  });
  const interactionRef = useRef<{
    end(): number;
  } | null>(null);

  useEffect(() => {
    // started loading
    if (loading) {
      interactionRef.current = measureInteraction();
    }
  }, [loading]);

  const ckey = useCustomerKey();

  useEffect(() => {
    const numProcesses = visibleProcesses.length;

    // finished loading
    if (!loading && interactionRef.current) {
      const duration = interactionRef.current?.end();
      // report loading time to honeycomb
      if (duration) {
        traceToHoneycomb({
          dataToTrace: {
            // span name
            name: `performance.${name} load time`,
            "performance.loading_duration": duration,
            "performance.num_processes": numProcesses,
            "performance.focus_range_hours": focusRangeHours,
            "performance.pageName": pageName,
            "performance.url": window.location.href,
            "performance.ckey": ckey,
          },
          callbackFn: () => {
            console.log(
              `⌚ traced performance.${name} loading_duration:`,
              duration,
              ` for ${numProcesses} processes and ${focusRangeHours} hours`
            );
          },
        });
        interactionRef.current = null;
      }
    }
  }, [
    loading,
    name,
    traceToHoneycomb,
    visibleProcesses.length,
    focusRangeHours,
    pageName,
    ckey,
  ]);
  return null;
}
/** in case you need to conditionally measure load time using useObserveComponentLoadTime */
export function ObserveComponentLoadTime({
  name,
  loading,
}: {
  name: string;
  loading: boolean;
}) {
  useObserveComponentLoadTime({
    name,
    loading,
  });
  return null;
}
